import { logger } from '@/libs/logger';
import { MediaGuideMetadata } from '@/utils/media/types';
import { routes } from '@/utils/routes';
import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import { MediaPackNarrator } from '@calm/evexia-web-sdk';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import { SxProps, Theme } from '@rhg/libs/material';
import { styleMaxLines } from '@rhg/libs/material/sx';
import { useRouter } from 'next/router';
import { RoundedCard } from '../rounded-card';

// On the narrator grid, clamp long names to two lines with an ellipsis rather
// than letting them grow the card and disturb the grid's row alignment.
// `wordBreak` lets a long, space-less name wrap-then-clamp (and so pick up the
// clamp's ellipsis) instead of overflowing the card horizontally.
const styleTitle = {
    ...styleMaxLines(2),
    wordBreak: 'break-word' as const,
};

/**
 * A narrator tap opens that narrator's landing feed ("Collection Details"),
 * whose `feedId` is the narratorId prefixed with `narrator-` (e.g. narratorId
 * `tamara` → feed `narrator-tamara`). The existing feed page + feed hook fetch
 * and render it — no narrator-specific route is needed.
 */
const NARRATOR_FEED_ID_PREFIX = 'narrator-';

export interface NarratorCardProps {
    narrator: MediaPackNarrator;
    /** Extra styles merged onto the RoundedCard wrapper. */
    sx?: SxProps<Theme>;
    /** Eager-load + high fetch priority for this card's image. Set only for the
     *  above-the-fold cards of a visible grid. */
    priority?: boolean;
}

/**
 * Circular avatar + name card for a narrator item nested inside a pack (e.g. the
 * "Artists" music pack). Renders the shared {@link RoundedCard} — matching the
 * `rounded-carousel` sizing — and navigates to the narrator detail page.
 */
export const NarratorCard = ({ narrator, sx, priority }: NarratorCardProps) => {
    const { push } = useRouter();
    const { trackPackItemEvent } = useTrackSessionEvent();
    const packTitle = usePackContext();

    const handleClick = () => {
        // Guard tracking so a throw here can never block navigation — firing
        // analytics is best-effort, opening the narrator feed is the point.
        try {
            trackPackItemEvent({
                eventName: EnumProductAnalyticsEvent.PackItemClicked,
                packTitle,
                // `trackPackItemEvent` only reads `title` off the item; a
                // narrator has no `MediaGuideMetadata` shape, so we pass just
                // the title.
                guide: { title: narrator.title } as MediaGuideMetadata,
            });
        } catch (err) {
            logger.error('Failed to track narrator click', {}, err as Error);
        }

        push(
            routes.discoverFeed(
                `${NARRATOR_FEED_ID_PREFIX}${narrator.narratorId}`
            )
        ).catch((err) =>
            logger.error('Failed to navigate to narrator', {}, err as Error)
        );
    };

    return (
        <RoundedCard
            title={narrator.title}
            imageSrc={narrator.image?.url}
            onClick={handleClick}
            priority={priority}
            sx={sx}
            titleSx={styleTitle}
        />
    );
};
